/**
 * Sprint T-16.1 · Automation Center · DlqTab.
 *
 * Lista + retry + discard de entries en Dead Letter Queue.
 */
import React, { useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, ShieldAlert } from "lucide-react";
import { WidgetCard, EmptyState, LoadingState, CitationBadge, MetricCard, WidgetGrid } from "../advisor";
import { useDlq, useDlqRetry, useDlqDiscard } from "../../hooks/useAutomation";
import { DlqCard } from "./shared/DomainCards";
import { DlqRetryDialog, DlqDiscardDialog } from "./shared/Dialogs";

const PAGE_SIZE = 25;

export default function DlqTab() {
  const [page, setPage] = useState(0);
  const [retryFor, setRetryFor] = useState(null);
  const [discardFor, setDiscardFor] = useState(null);

  const q = useDlq({ limit: PAGE_SIZE, skip: page * PAGE_SIZE });
  const retry = useDlqRetry();
  const discard = useDlqDiscard();

  const items = q.data?.items || [];
  const total = q.data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const pending = items.filter((i) => !i.reviewed_at).length;

  const doRetry = async () => {
    try {
      const res = await retry.mutateAsync(retryFor.id);
      toast.success(`Reintento encolado · ${res.run_id?.slice(0, 8)}`);
      setRetryFor(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al reintentar");
    }
  };

  const doDiscard = async () => {
    try {
      await discard.mutateAsync(discardFor.id);
      toast.success("Entrada descartada");
      setDiscardFor(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al descartar");
    }
  };

  return (
    <section className="space-y-4" data-testid="automation-dlq-tab">
      <WidgetGrid cols="md:grid-cols-3">
        <MetricCard label="Total DLQ" value={total} hint="acumulado" testId="dlq-total" />
        <MetricCard label="Sin revisar" value={pending} hint="requiere acción" testId="dlq-pending" />
        <MetricCard
          label="Revisadas"
          value={items.length - pending}
          hint="página actual"
          testId="dlq-reviewed"
        />
      </WidgetGrid>

      <WidgetCard
        title={`Dead Letter Queue · ${total} entries`}
        description="Ejecuciones que agotaron reintentos o fallaron con noretry_on."
        citations={["/api/automation/dlq"]}
        testId="widget-dlq-list"
      >
        {q.isLoading && <LoadingState />}
        {q.error && <EmptyState message="No se pudo cargar la DLQ." />}
        {q.data && total === 0 && (
          <div
            data-testid="dlq-empty"
            className="rounded border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs text-emerald-500"
          >
            <ShieldAlert className="mr-1 inline h-3 w-3" />
            Sin fallos en DLQ · el motor está sano.
          </div>
        )}
        {items.length > 0 && (
          <div className="space-y-3" data-testid="dlq-list">
            {items.map((entry) => (
              <DlqCard
                key={entry.id}
                entry={entry}
                onRetry={setRetryFor}
                onDiscard={setDiscardFor}
              />
            ))}
          </div>
        )}

        {total > PAGE_SIZE && (
          <div
            data-testid="dlq-pagination"
            className="mt-3 flex items-center justify-between text-xs"
          >
            <span className="text-[var(--ag-text-dim)]">
              Mostrando {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} de {total}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                data-testid="dlq-page-prev"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="inline-flex items-center gap-1 rounded border border-[var(--ag-border)] px-2 py-0.5 text-[10px] disabled:opacity-30"
              >
                <ChevronLeft className="h-3 w-3" /> Anterior
              </button>
              <span className="px-2 text-[10px] tabular-nums">
                pág {page + 1} / {totalPages}
              </span>
              <button
                type="button"
                data-testid="dlq-page-next"
                onClick={() => setPage((p) => p + 1)}
                disabled={(page + 1) * PAGE_SIZE >= total}
                className="inline-flex items-center gap-1 rounded border border-[var(--ag-border)] px-2 py-0.5 text-[10px] disabled:opacity-30"
              >
                Siguiente <ChevronRight className="h-3 w-3" />
              </button>
            </div>
          </div>
        )}
      </WidgetCard>

      {retryFor && (
        <DlqRetryDialog
          entry={retryFor}
          onClose={() => setRetryFor(null)}
          onConfirm={doRetry}
          pending={retry.isPending}
        />
      )}
      {discardFor && (
        <DlqDiscardDialog
          entry={discardFor}
          onClose={() => setDiscardFor(null)}
          onConfirm={doDiscard}
          pending={discard.isPending}
        />
      )}

      <footer className="border-t border-[var(--ag-border)] pt-3">
        <CitationBadge sources={["retry/discard auditado T-14", "audit.automation.dlq.*"]} />
      </footer>
    </section>
  );
}
